import { ReactElement } from 'react';
import { capitalize, upperCaseFirst } from '@client/common/utils/string/string';
import { useTranslate } from '@client/i18n/hooks';
import { PRODUCT_BOX_STYLE_TYPE } from '@lib/components/ProductList';
import classNames from 'classnames';
import { Badge, BADGE_SIZE } from 'b2b-components/theme/components/Badge';
import { Button, BUTTON_ICON_POSITION, BUTTON_SIZE } from 'b2b-components/theme/components/Button';
import {
  BUTTON_ICON_ICON_NAME,
  BUTTON_ICON_SIZE,
  BUTTON_ICON_STYLE_TYPE,
  ButtonIcon,
} from 'b2b-components/theme/components/ButtonIcon';
import { Select } from 'b2b-components/theme/components/Form/Select';
import { Icon, ICON_NAME, ICON_SIZE } from 'b2b-components/theme/components/Icon';
import { Modal, MODAL_TYPE } from 'b2b-components/theme/components/Modal';
import { Props } from './ResultsViewOptions.types';
import './ResultsViewOptions.scss';

export const ResultsViewOptions = ({
  countFilters,
  pageSizeOptions,
  sortingOptions,
  viewType,
  renderMobileFilters,
  onViewTypeChange,
  onPageSizeChange,
  onSortingChange,
  pageSize,
  sorting,
}: Props): ReactElement => {
  const t = useTranslate();
  const selectOrderId = 'catalog/ResultsViewOptions/selectOrderId';
  const selectPerPageId = 'catalog/ResultsViewOptions/selectPerPageId';

  return (
    <form className='ResultsViewOptions'>
      <div className='ResultsViewOptions-Sorting'>
        <label className='Catalog-FormLabel' htmlFor='sort'>
          {t('Sort order')}:
        </label>

        <Select
          selectId={selectOrderId}
          onChange={onSortingChange}
          options={sortingOptions}
          value={sorting || 0}
          dataAttributes={{ 'data-testid': 'catalogSortOrderSelect' }}
        />
      </div>

      <div className='ResultsViewOptions-ItemsPerPage'>
        <label className='Catalog-FormLabel' htmlFor='perPage'>
          {t('Articles per page')}:
        </label>

        <Select
          selectId={selectPerPageId}
          onChange={(pageSize) => onPageSizeChange(parseInt(`${pageSize}`, 10))}
          options={pageSizeOptions}
          value={pageSize}
          dataAttributes={{ 'data-testid': 'catalogArticlesPerPageSelect' }}
        />
      </div>

      <div className='ResultsViewOptions-Filters'>
        <Modal
          className='Catalog-MobileFilters'
          content={renderMobileFilters}
          hideCloseButton
          triggerObject={
            <Button
              iconPosition={BUTTON_ICON_POSITION.right}
              size={BUTTON_SIZE.small}
            >
              <Icon name={ICON_NAME.filter} size={ICON_SIZE.small} />

              <Badge size={BADGE_SIZE.small}>{countFilters}</Badge>

              {t('Filters')}
            </Button>
          }
          type={MODAL_TYPE.rightSide}
        />
      </div>

      <div className='ResultsViewOptions-ViewType'>
        <div className='Catalog-FormLabel'>{t('View type')}:</div>

        <div className='ResultsViewOptions-ViewTypeOptions' role='radiogroup'>
          {Object.values(PRODUCT_BOX_STYLE_TYPE).map((type) => {
            const isChecked = viewType === type;

            return (
              <ButtonIcon
                aria-checked={isChecked}
                icon={BUTTON_ICON_ICON_NAME[`view${upperCaseFirst(type)}`]}
                onClick={() => onViewTypeChange(type)}
                role='radio'
                size={BUTTON_ICON_SIZE.medium}
                styleType={BUTTON_ICON_STYLE_TYPE.negative}
                className={`ResultsViewOptions-ViewTypeOption ResultsViewOptions-ViewTypeOption--${capitalize(type)}`}
                iconClassName={classNames(
                  'ResultsViewOptions-ViewTypeOptionIcon',
                  {
                    'ResultsViewOptions-ViewTypeOptionIcon--Selected': isChecked,
                  },
                )}
                key={type}
              />
            );
          })}
        </div>
      </div>
    </form>
  );
};
